import { ref, computed } from 'vue'

const GOOGLE_CLIENT_ID = '395383920665-qbqvhb0tlpb7a9g2oiul4q1m2ttmvttp.apps.googleusercontent.com'

const user = ref(null)
const token = ref(null)
const loading = ref(false)
const error = ref(null)

const isAuthenticated = computed(() => !!token.value && !!user.value)

/**
 * Initialize Google Sign-In library
 */
export function initializeGoogleAuth() {
  return new Promise((resolve, reject) => {
    // Load Google Sign-In library
    const script = document.createElement('script')
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.defer = true
    script.onload = () => {
      if (window.google) {
        resolve()
      } else {
        reject(new Error('Failed to load Google Sign-In library'))
      }
    }
    script.onerror = () => reject(new Error('Failed to load Google Sign-In script'))
    document.head.appendChild(script)
  })
}

/**
 * Handle Google Sign-In response
 */
export async function handleGoogleSignIn(response, rememberMe = false) {
  loading.value = true
  error.value = null

  try {
    const credential = response.credential
    if (!credential) {
      throw new Error('No credential received from Google')
    }

    // Exchange Google token for session token
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        googleToken: credential,
        rememberMe
      })
    })

    if (!res.ok) {
      const errorData = await res.json()
      throw new Error(errorData.error || 'Login failed')
    }

    const data = await res.json()
    
    // Store session token and user info
    token.value = data.sessionToken
    user.value = data.user
    localStorage.setItem('session_token', data.sessionToken)
    localStorage.setItem('session_user', JSON.stringify(data.user))
    localStorage.setItem('session_expires_at', data.expiresAt)

    return user.value
  } catch (err) {
    error.value = err.message
    console.error('Error handling Google sign-in:', err)
    throw err
  } finally {
    loading.value = false
  }
}

/**
 * Sign out user
 */
export async function signOut() {
  try {
    // Call logout endpoint if we have a token
    if (token.value) {
      await fetch('/api/auth/logout', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token.value}`
        }
      })
    }
  } catch (err) {
    console.error('Error during logout:', err)
  } finally {
    // Clear local state regardless of API call result
    token.value = null
    user.value = null
    localStorage.removeItem('session_token')
    localStorage.removeItem('session_user')
    localStorage.removeItem('session_expires_at')
    
    // Sign out from Google
    if (window.google) {
      window.google.accounts.id.revoke('', () => {
        console.log('Signed out from Google')
      })
    }
  }
}

/**
 * Get current user
 */
export function getCurrentUser() {
  return user.value
}

/**
 * Get auth token
 */
export function getAuthToken() {
  return token.value
}

/**
 * Restore session from localStorage
 */
export async function restoreSession() {
  const storedToken = localStorage.getItem('session_token')
  const storedUser = localStorage.getItem('session_user')
  const expiresAt = localStorage.getItem('session_expires_at')
  
  if (storedToken && storedUser) {
    // Check if session is expired
    if (expiresAt && new Date(expiresAt) < new Date()) {
      clearAuth()
      return false
    }
    
    try {
      // Verify session with backend
      const res = await fetch('/api/auth/verify', {
        headers: {
          'Authorization': `Bearer ${storedToken}`
        }
      })
      
      if (res.ok) {
        const data = await res.json()
        token.value = storedToken
        user.value = JSON.parse(storedUser)
        return true
      } else {
        clearAuth()
        return false
      }
    } catch (e) {
      console.error('Failed to verify session:', e)
      // If verification fails (e.g., network error), still restore from localStorage
      // The API will reject the token if it's actually invalid
      token.value = storedToken
      user.value = JSON.parse(storedUser)
      return true
    }
  }
  
  return false
}

/**
 * Store user info
 */
export function storeUserInfo(userInfo) {
  user.value = userInfo
  localStorage.setItem('auth_user', JSON.stringify(userInfo))
}

/**
 * Clear authentication (logout)
 */
export function clearAuth() {
  user.value = null
  token.value = null
  localStorage.removeItem('session_token')
  localStorage.removeItem('session_user')
  localStorage.removeItem('session_expires_at')
}

export {
  user,
  token,
  loading,
  error,
  isAuthenticated
}
